import React, { createContext, useContext, useState, useEffect } from "react";
import service from "../service";
import { useAuth } from "./auth";
import { transformConditionById, makeBlankCondition } from "../utils";
import { v4 as uuidv4 } from "uuid";

const TagAndSegmentContext = createContext();

const TagAndSegmentContextProvider = ({ children }) => {
  const { user } = useAuth();
  const [segmentConditions, setSegmentConditions] = useState(
    makeBlankCondition()
  );
  const [tagArray, setTagArray] = useState([]);
  const [segmentArray, setSegmentArray] = useState([]);
  const [segmentName, setSegmentName] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const readAllTags = async (pageNumber, pageSize) => {
    try {
      const tags = await service.readAllTags(pageNumber, pageSize);
      if (tags) {
        setIsLoading(false);
        const tagsArray = Array.isArray(tags) ? tags : [];
        setTagArray(tagsArray);
      }

    } catch {
      console.error('Unable to retrieve Tags')
    }
  };

  const readAllSegments = async () => {
    try {
      const segmentsRes = await service.readAllSegments();
      const segments = Array.isArray(segmentsRes) ? segmentsRes : [];
      setSegmentArray(segments);
    } catch {
      console.error('Unable to retrieve Segments')
    }
  };

  const readTagById = async (id) => {
    const response = await service.readTagById(id);
    const tag = await response.json();
    return tag;
  };

  const handleCreateTag = async (data) => {
    try {
      await service.createTag({ ...data, createdBy: user.id });
      await readAllTags();
      return { success: true };
    } catch (error) {
      console.error('Error creating tag:', error);
      return {
        success: false,
        error: error.message || 'An error occurred while creating the tag'
      };
    }
  };

  function onConditionChange(updatedCondition) {
    setSegmentConditions((prev) => {
      const cloned = structuredClone(prev);

      // We'll pass a transformFn that checks the node’s ID
      // and returns the updated object if it matches:
      const transformFn = (node) => {
        // If we are here, we know node.id === updatedCondition.id
        return { ...node, ...updatedCondition };
      };

      const newSegmentConditions = transformConditionById(
        cloned,
        updatedCondition.id,
        transformFn
      );

      return newSegmentConditions || {};
    });
  }

  function onConditionDelete(targetId) {
    setSegmentConditions((prev) => {
      const cloned = structuredClone(prev);

      // A transformFn that always returns null => removing the matching node
      const transformFn = () => null;

      let newSegmentConditions = transformConditionById(
        cloned,
        targetId,
        transformFn
      );

      // ---- Final root-level check ----
      if (!newSegmentConditions) {
        // If the entire structure got removed
        newSegmentConditions = makeBlankCondition();
      }

      // If it's a group with 0 children,
      // we can do the same logic: transform it into a single blank condition
      if (
        newSegmentConditions.type &&
        Array.isArray(newSegmentConditions.conditions) &&
        newSegmentConditions.conditions.length === 0
      ) {
        newSegmentConditions = makeBlankCondition();
      }

      // If it's a group with just 1 child, simplify it by returning the child
      if (
        newSegmentConditions.type &&
        Array.isArray(newSegmentConditions.conditions) &&
        newSegmentConditions.conditions.length === 1
      ) {
        newSegmentConditions = newSegmentConditions.conditions[0];
      }

      return newSegmentConditions;
    });
  }

  function handleAddOr() {
    setSegmentConditions((prev) => {
      // Clone so as not to mutate state directly
      const cloned = structuredClone(prev);

      // 1. If 'cloned' is a single condition (no .type):
      if (!cloned.type) {
        return {
          id: uuidv4(),
          type: "OR",
          conditions: [
            cloned, // the old single condition
            makeBlankCondition(),
          ],
        };
      }

      // 2. If 'cloned' is a top-level group but not OR (e.g., AND, or anything else):
      if (cloned.type !== "OR") {
        return {
          id: uuidv4(),
          type: "OR",
          conditions: [
            cloned, // the old group as the first item
            makeBlankCondition(),
          ],
        };
      }

      // 3. If 'cloned' is already an OR group:
      if (cloned.type === "OR") {
        cloned.conditions.push(makeBlankCondition());
        return cloned;
      }

      // Fallback: if we somehow got here with an unrecognized structure,
      // just return 'cloned' unchanged
      return cloned;
    });
  }

  function handleAddAnd(nodeId) {
    setSegmentConditions((prev) => {
      const cloned = structuredClone(prev);

      // 1. If 'cloned' is a single condition (no .type):
      if (!cloned.type) {
        return {
          id: uuidv4(),
          type: "AND",
          conditions: [
            cloned, // the old single condition
            makeBlankCondition(),
          ],
        };
      }

      if (cloned.type === "AND") {
        return {
          id: uuidv4(),
          type: "AND",
          conditions: [...cloned.conditions, makeBlankCondition()],
        };
      }

      // 3. Top-level OR: find the child matching nodeId
      if (cloned.type === "OR") {
        const idx = cloned.conditions.findIndex((child) => child.id === nodeId);
        if (idx === -1) {
          // No matching node found, return unchanged
          return cloned;
        }

        const child = cloned.conditions[idx];

        // If the child is already an AND group, just add a blank condition
        if (child.type === "AND") {
          child.conditions.push({
            id: uuidv4(),
            field: "Tags",
            operator: "isTagged",
            value: "",
          });
        } else {
          // Otherwise, wrap the child in a new AND group
          cloned.conditions[idx] = {
            id: uuidv4(),
            type: "AND",
            conditions: [
              child,
              {
                id: uuidv4(),
                field: "Tags",
                operator: "isTagged",
                value: "",
              },
            ],
          };
        }
        return cloned;
      }

      // Fallback: if we somehow got here with an unrecognized structure,
      // just return 'cloned' unchanged
      return cloned;
    });
  }

  return (
    <TagAndSegmentContext.Provider
      value={{
        readAllTags,
        readTagById,
        tagArray,
        handleCreateTag,
        readAllSegments,
        segmentArray,
        setSegmentArray,
        segmentConditions,
        setSegmentConditions,
        onConditionChange,
        onConditionDelete,
        handleAddOr,
        handleAddAnd,
        segmentName,
        setSegmentName,
        isLoading,
        setIsLoading,
      }}
    >
      {children}
    </TagAndSegmentContext.Provider>
  );
};

const useTagAndSegment = () => useContext(TagAndSegmentContext);

export { useTagAndSegment };
export default TagAndSegmentContextProvider;
